import { EXAM_BY_ID, EXAMS } from "../data/exams";
import { LESSONS } from "../data/lessons";
import { QUESTION_BY_ID } from "../data/questions";
import type { ExamId, Question } from "../data/types";
import type { Attempt, Progress } from "./progress";
import { isCorrect } from "./scoring";
import { newSeed } from "./random";
import type { L } from "../i18n/lang";
import { buildFixedSession, type Session } from "./session";

export { DIAGNOSTIC_IDS } from "../data/diagnostic-ids";
import { DIAGNOSTIC_IDS } from "../data/diagnostic-ids";
export const PASS_PER_LEVEL = 2;

export function diagnosticQuestions(): Question[] {
  return EXAMS.flatMap((e) => DIAGNOSTIC_IDS[e.id].map((id) => QUESTION_BY_ID.get(id)).filter((q): q is Question => !!q));
}

export function buildDiagnostic(seed = newSeed()): Session {
  return buildFixedSession("diagnostic", { vi: "Kiểm tra trình độ", en: "Placement test" }, diagnosticQuestions(), seed);
}

export interface Placement {
  /** Số câu đúng theo từng cấp. */
  perLevel: { exam: ExamId; correct: number; total: number }[];
  /** Chứng chỉ nên nhắm tới tiếp theo. */
  target: ExamId;
  /** Chặng lộ trình nên bắt đầu. */
  stage: number;
  firstLessonId: string;
  headline: L;
  advice: L;
}

export function placementFromAttempt(a: Pick<Attempt, "answers">): Placement {
  const perLevel = EXAMS.map((e) => {
    const ids = DIAGNOSTIC_IDS[e.id];
    const correct = ids.filter((id) => {
      const q = QUESTION_BY_ID.get(id);
      return q && isCorrect(a.answers[id] ?? [], q.answer);
    }).length;
    return { exam: e.id, correct, total: ids.length };
  });

  const gap = perLevel.find((l) => l.correct < PASS_PER_LEVEL);
  const target = gap?.exam ?? "ccar-p";
  const exam = EXAM_BY_ID[target];
  // Hổng ngay cấp 1 với ≤ 1 câu đúng → bắt đầu từ Nhập môn.
  const stage = gap ? (exam.level === 1 && gap.correct <= 1 ? 0 : exam.level) : 4;
  const firstLessonId = LESSONS.find((l) => l.stage === stage)?.id ?? LESSONS[0]!.id;

  let headline: L;
  let advice: L;
  if (stage === 0) {
    headline = { vi: "Bắt đầu từ Nhập môn", en: "Start with the Basics" };
    advice = {
      vi: "Hãy đi tuần tự từ chặng Nhập môn — các bài ngắn sẽ xây nền tảng prompt và dùng AI có trách nhiệm trước khi lên Associate.",
      en: "Work through the Basics stage first — short lessons build prompting fundamentals and responsible AI use before Associate.",
    };
  } else if (!gap) {
    headline = { vi: "Bạn đã ở trình độ Architect", en: "You're already at Architect level" };
    advice = {
      vi: "Nền tảng rất tốt. Tập trung vào Architect Professional: governance, eval và vận hành — rồi thi thử để kiểm chứng.",
      en: "Strong foundation. Focus on Architect Professional — governance, evals and operations — then take a mock exam to confirm.",
    };
  } else {
    const passed = EXAMS.filter((e) => e.level < exam.level).map((e) => e.code).join(", ");
    headline = { vi: `Mục tiêu tiếp theo: ${exam.code}`, en: `Next target: ${exam.code}` };
    advice = passed
      ? { vi: `Bạn đã nắm vững cấp ${passed}. Bắt đầu chặng ${stage} của lộ trình để chinh phục ${exam.code}.`, en: `You've mastered ${passed}. Start stage ${stage} of the path to tackle ${exam.code}.` }
      : { vi: `Bạn đã có nền tảng cơ bản. Bắt đầu chặng ${stage} để chinh phục ${exam.code}.`, en: `You have the basics. Start stage ${stage} to tackle ${exam.code}.` };
  }
  return { perLevel, target, stage, firstLessonId, headline, advice };
}

export function latestDiagnostic(p: Progress): Attempt | undefined {
  return p.attempts.find((a) => a.kind === "diagnostic");
}
